import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button as MuiButton,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  withStyles,
} from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@material-ui/icons';
import styles from './resolution-dialog.style';
import { TextField, Button } from '@orijinworks/frontend-commons';
import { QUEUE_STATUS } from '../../constants/lms-course-review.constants';
import QueueStatusChip from '../queue-status-chip/queue-status-chip';
import SuggestionCard from '../suggestion-card/suggestion-card';
import ResourceSearchResults from '../resource-search-results/resource-search-results';
import SelectedResourceCard from '../selected-resource-card/selected-resource-card';
import NewResourceForm from '../new-resource-form/new-resource-form';

const ResolutionDialog = ({
  classes,
  open,
  item,
  isPending,
  showSuggestion,
  suggestion,
  onDismissSuggestion,
  onAcceptSuggestion,
  searchInput,
  onSearchInputChange,
  searchOptions,
  searchLoading,
  selectedResource,
  onSelectResource,
  onClearSelectedResource,
  onLinkSelected,
  createForm,
  onCreateFormChange,
  onCreateAndLink,
  actionLoading,
  onClose,
}) => {
  const [showManual, setShowManual] = useState(false);

  // Default to the manual options when there is no suggestion to accept,
  // otherwise keep them progressively disclosed behind a toggle.
  useEffect(() => {
    if (open) {
      setShowManual(!showSuggestion);
    }
  }, [open, showSuggestion, item]);

  if (!item) return null;

  const titleId = 'lms-course-review-resolution-dialog-title';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby={titleId}
      data-testid="lms-course-review-resolution-dialog"
    >
      <DialogTitle id={titleId}>
        {isPending ? 'Resolve queue item' : 'Queue item details'}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.courseSummary}>
          <Typography variant="overline" color="textSecondary">Canvas course</Typography>
          <Typography variant="h6">{item.canvasCourseName}</Typography>
          <Typography variant="caption" color="textSecondary">
            Canvas ID: {item.canvasCourseId}
          </Typography>
          <Box marginTop={1.5}>
            <QueueStatusChip reviewStatus={item.reviewStatus} status={item.status} />
          </Box>
        </Box>

        {item.status === QUEUE_STATUS.RESOLVED && (
          <Box marginBottom={3} data-testid="resolved-summary">
            <Typography variant="subtitle2" className={classes.sectionLabel}>Linked course</Typography>
            {item.linkedResourceName ? (
              <Typography variant="body1">{item.linkedResourceName}</Typography>
            ) : null}
            <Typography variant="body2" color="textSecondary">
              Course ID: {item.linkedResourceId ?? '—'}
            </Typography>
            {item.resolvedAt ? (
              <Typography variant="caption" color="textSecondary">
                Resolved: {new Date(item.resolvedAt).toLocaleString()}
              </Typography>
            ) : null}
          </Box>
        )}

        {isPending && (
          <>
            {showSuggestion && (
              <Box marginBottom={2}>
                <Typography variant="subtitle2" className={classes.sectionLabel}>
                  Suggested match
                </Typography>
                <SuggestionCard
                  resource={suggestion}
                  onAccept={onAcceptSuggestion}
                  onDismiss={onDismissSuggestion}
                  loading={actionLoading}
                />
              </Box>
            )}

            <Box className={classes.manualToggle}>
              <MuiButton
                onClick={() => setShowManual((prev) => !prev)}
                endIcon={showManual ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                data-testid="toggle-manual-options"
                style={{ textTransform: 'none' }}
              >
                {showSuggestion ? 'Link manually instead' : 'Link manually'}
              </MuiButton>
            </Box>

            <Collapse in={showManual} timeout="auto" unmountOnExit>
              <Box className={classes.manualSection}>
                <Typography variant="subtitle1" className={classes.sectionLabel}>
                  Search existing courses
                </Typography>
                <Box className={classes.fieldStack}>
                  <TextField
                    fullWidth
                    label="Search by course name"
                    value={searchInput}
                    onChange={(e) => onSearchInputChange(e.target.value)}
                    data-testid="manual-resource-search"
                  />
                  <ResourceSearchResults
                    options={searchOptions}
                    loading={searchLoading}
                    searchInput={searchInput}
                    selectedResource={selectedResource}
                    onSelect={onSelectResource}
                  />
                  <SelectedResourceCard
                    resource={selectedResource}
                    onClear={onClearSelectedResource}
                  />
                  <Box>
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={!selectedResource?.id || actionLoading}
                      onClick={onLinkSelected}
                      data-testid="link-selected-resource-btn"
                    >
                      Link selected course
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Divider className={classes.sectionDivider} />

              <Box>
                <Typography variant="subtitle1" className={classes.sectionLabel}>
                  Create new course
                </Typography>
                <NewResourceForm
                  form={createForm}
                  onChange={onCreateFormChange}
                  onSubmit={onCreateAndLink}
                  loading={actionLoading}
                />
              </Box>
            </Collapse>
          </>
        )}

        <Box className={classes.footerActions}>
          <Button variant="outlined" onClick={onClose} data-testid="close-resolution-dialog">
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ResolutionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  isPending: PropTypes.bool,
  showSuggestion: PropTypes.bool,
  suggestion: PropTypes.object,
  onDismissSuggestion: PropTypes.func.isRequired,
  onAcceptSuggestion: PropTypes.func.isRequired,
  searchInput: PropTypes.string,
  onSearchInputChange: PropTypes.func.isRequired,
  searchOptions: PropTypes.array,
  searchLoading: PropTypes.bool,
  selectedResource: PropTypes.object,
  onSelectResource: PropTypes.func.isRequired,
  onClearSelectedResource: PropTypes.func.isRequired,
  onLinkSelected: PropTypes.func.isRequired,
  createForm: PropTypes.object.isRequired,
  onCreateFormChange: PropTypes.func.isRequired,
  onCreateAndLink: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

ResolutionDialog.defaultProps = {
  item: null,
  isPending: false,
  showSuggestion: false,
  suggestion: null,
  searchInput: '',
  searchOptions: [],
  searchLoading: false,
  selectedResource: null,
  actionLoading: false,
};

export default withStyles(styles)(ResolutionDialog);
